import { supabase } from './supabase.js';
import { assertPaymentsEnabled, LAUNCH_PHASE_PAYMENTS_MSG } from './launchPhaseClient.js';
import { getPublicSiteBase } from '../constants/siteConfig.js';

export function formatGroovesPackEuro(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

/** Base URL para success/cancel de Stripe (`https://dreamvinyl.es` en producción). */
export function groovesCheckoutReturnBaseUrl() {
  return getPublicSiteBase();
}

export function groovesPackPriceFromRow(pack) {
  if (!pack) return null;
  const eur = Number(pack.price_eur);
  if (Number.isFinite(eur) && eur >= 0) return formatGroovesPackEuro(eur);
  const cents = Number(pack.price_cents);
  if (Number.isFinite(cents) && cents >= 0) return formatGroovesPackEuro(cents / 100);
  const display = pack.price_display != null ? String(pack.price_display).trim() : '';
  if (display) return display;
  return null;
}

export function groovesPackDisplayLabel(pack) {
  const label = String(pack?.label ?? '').trim();
  const amount = Number(pack?.grooves_amount);
  if (label && Number.isFinite(amount) && amount > 0 && !/\d+\s*Grooves/i.test(label)) {
    return `${label} — ${amount} Grooves`;
  }
  return label || (Number.isFinite(amount) ? `${amount} Grooves` : 'Paquete');
}

export function groovesPackDisplayPrice(pack) {
  return groovesPackPriceFromRow(pack) || null;
}

/** Mensaje legible para errores de invoke / Edge Function. */
export function mapGroovesCheckoutError(error) {
  const code = String(error ?? '').trim();
  const lower = code.toLowerCase();
  if (!code) return 'No se pudo iniciar el pago.';
  if (lower.includes('edge function returned a non-2xx')) {
    return 'No se pudo iniciar el pago. Inténtalo de nuevo en unos segundos.';
  }
  if (lower.includes('phone_not_verified') || lower.includes('teléfono')) {
    return 'Verifica tu teléfono en Ajustes antes de comprar Grooves.';
  }
  if (lower.includes('unauthorized') || lower.includes('jwt') || lower.includes('sesión')) {
    return 'Inicia sesión para comprar Grooves.';
  }
  if (lower.includes('invalid_pack') || lower.includes('paquete no disponible')) {
    return 'Este paquete no está disponible. Prueba otro o contacta con soporte.';
  }
  if (lower.includes('launch_phase_blocked') || lower.includes('fase del lanzamiento')) {
    return LAUNCH_PHASE_PAYMENTS_MSG;
  }
  if (lower.includes('invalid api key') || lower.includes('whsec_')) {
    return 'La configuración de pago en el servidor no es correcta. Revisa las claves en el panel de administración.';
  }
  if (lower.includes('stripe_misconfigured') || lower.includes('clave de pago')) {
    return 'La configuración de pago no es correcta. Contacta con soporte o revisa las claves Stripe en Supabase.';
  }
  if (lower.includes('payment_not_paid') || lower.includes('aún no marca')) {
    return 'El pago se está procesando. Espera unos segundos.';
  }
  if (lower.includes('stripe') || lower.includes('pago')) {
    return 'No se pudo abrir la pasarela de pago. Inténtalo más tarde.';
  }
  if (lower.includes('failed to fetch') || lower.includes('network')) {
    return 'Error de conexión. Revisa tu red e inténtalo de nuevo.';
  }
  return code;
}

/** Extrae mensaje del cuerpo JSON cuando invoke devuelve error HTTP. */
export async function extractInvokeErrorMessage(error) {
  if (!error) return 'Error desconocido';
  let msg = error.message || 'Error';
  if (error.context && typeof error.context.json === 'function') {
    try {
      const j = await error.context.json();
      if (j && typeof j.message === 'string') msg = j.message;
      else if (j && typeof j.error === 'string') msg = j.error;
    } catch (_) {}
  }
  return msg;
}

/** Lista paquetes activos con price_id Stripe real y precio EUR en BD. */
export async function fetchGroovesStripePacks() {
  const { data, error } = await supabase
    .from('grooves_stripe_packs')
    .select('code, label, grooves_amount, stripe_price_id, activo, sort_order, price_eur')
    .eq('activo', true)
    .order('sort_order', { ascending: true });
  if (error) return { data: [], error: error.message };
  const rows = (data ?? []).filter(r => typeof r.stripe_price_id === 'string' && r.stripe_price_id.startsWith('price_'));
  return { data: rows, error: null };
}

/** Crea sesión Stripe Checkout vía Edge Function `create-checkout-grooves`. */
export async function createGroovesCheckoutSession(packCode) {
  const code = String(packCode ?? '').trim();
  if (!code) return { url: null, error: 'missing_pack' };

  const phaseBlock = await assertPaymentsEnabled();
  if (phaseBlock) return { url: null, error: phaseBlock };

  const { data, error } = await supabase.functions.invoke('create-checkout-grooves', {
    body: {
      pack_code: code,
      return_base_url: groovesCheckoutReturnBaseUrl(),
    },
  });

  if (data && typeof data === 'object') {
    if (data.error) {
      return {
        url: null,
        error: mapGroovesCheckoutError(data.message || data.error),
      };
    }
    if (data.ok && data.url) {
      return { url: data.url, error: null, sessionId: data.session_id ?? null };
    }
  }

  if (error) {
    const raw = await extractInvokeErrorMessage(error);
    return { url: null, error: mapGroovesCheckoutError(raw) };
  }

  return { url: null, error: mapGroovesCheckoutError(data?.message || 'no_url') };
}

/**
 * Crea la sesión y redirige a Stripe Checkout en la misma pestaña.
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export async function startGroovesPackCheckout(packCode) {
  const { url, error } = await createGroovesCheckoutSession(packCode);
  if (!url) {
    return { ok: false, error: error || 'No se pudo iniciar el pago.' };
  }
  window.location.assign(url);
  return { ok: true };
}

/**
 * @deprecated Usar startGroovesPackCheckout
 */
export async function redirectToGroovesPackCheckout(packCode) {
  return startGroovesPackCheckout(packCode);
}

/**
 * Abona Grooves verificando el pago en Stripe (respaldo si el webhook tarda o falla).
 * Idempotente: seguro llamar varias veces con la misma session_id.
 */
export async function fulfillGroovesCheckoutSession(stripeSessionId) {
  const sid = String(stripeSessionId ?? '').trim();
  if (!sid) {
    return { ok: false, fulfilled: false, grooves_amount: 0, error: 'missing_session' };
  }

  const { data, error } = await supabase.functions.invoke('fulfill-grooves-checkout', {
    body: { session_id: sid },
  });

  if (data && typeof data === 'object') {
    if (data.ok) {
      return {
        ok: true,
        fulfilled: !!data.fulfilled,
        grooves_amount: Number(data.grooves_amount) || 0,
        pack_code: data.pack_code ?? null,
        error: null,
      };
    }
    if (data.error) {
      return {
        ok: false,
        fulfilled: false,
        grooves_amount: 0,
        error: mapGroovesCheckoutError(data.message || data.error),
      };
    }
  }

  if (error) {
    const raw = await extractInvokeErrorMessage(error);
    return {
      ok: false,
      fulfilled: false,
      grooves_amount: 0,
      error: mapGroovesCheckoutError(raw),
    };
  }

  return {
    ok: false,
    fulfilled: false,
    grooves_amount: 0,
    error: mapGroovesCheckoutError('fulfill_failed'),
  };
}

/** Estado de una sesión de checkout propia (tras volver de Stripe). */
export async function fetchGroovesCheckoutSessionStatus(stripeSessionId) {
  const sid = String(stripeSessionId ?? '').trim();
  if (!sid) return { data: null, error: 'missing_session' };
  const { data, error } = await supabase
    .from('grooves_stripe_checkout_sessions')
    .select('pack_code, grooves_amount, fulfilled_at, created_at')
    .eq('stripe_session_id', sid)
    .maybeSingle();
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}
